import {
  CollectionImportChangeDTO,
  CollectionImportResultDTO,
  CollectionImportRowDTO,
  DepartmentTreeNodeDTO,
} from '@/api/trafikportalen';
import { create } from 'zustand';
import { MappedColumns } from '@/components/Import/ImportColumnMapper';
import { Columns } from '@/components/Import/ImportFileForm';

interface ImportFileData {
  importFile?: File;
  importHeaders?: string[];
  importRows?: string[][];
  importColumns?: Columns[];
}

interface ImportStatus {
  isOnNextStep?: boolean;
  isAllColumnsValidated?: boolean;
  isValidating?: boolean;
}

export interface Import {
  file?: ImportFileData;
  status?: ImportStatus;
  department?: DepartmentTreeNodeDTO;
  mappedColumns?: MappedColumns;
}

interface ImportFileStore {
  import: Import;
  importData: CollectionImportRowDTO[];
  originalImportDataByRowNumber: Record<number, CollectionImportRowDTO>;
  validationResult: CollectionImportResultDTO | null;
  failedChanges: CollectionImportChangeDTO[];
  successChanges: CollectionImportChangeDTO[];
  editedRows: Set<number>;
  setImportData: (data: CollectionImportRowDTO[]) => void;
  updateImportRow: (
    rowNumber: number,
    updatedRow: CollectionImportRowDTO,
  ) => void;
  markRowEdited: (rowNumber: number) => void;
  clearEditedRows: () => void;
  setValidationResult: (result: CollectionImportResultDTO) => void;
  setValidating: (isValidating: boolean) => void;
  update: (data: Partial<Import>) => void;
  reset: () => void;
}

export const useImportFileStore = create<ImportFileStore>((set) => ({
  import: {},
  importData: [],
  originalImportDataByRowNumber: {},
  validationResult: null,
  failedChanges: [],
  successChanges: [],
  editedRows: new Set<number>(),

  setImportData: (data: CollectionImportRowDTO[]) =>
    set({
      importData: data,
      originalImportDataByRowNumber: data.reduce<
        Record<number, CollectionImportRowDTO>
      >((acc, row) => {
        acc[row.row_number] = structuredClone(row);
        return acc;
      }, {}),
    }),

  updateImportRow: (rowNumber: number, updatedRow: CollectionImportRowDTO) =>
    set((state) => ({
      importData: state.importData.map((row) =>
        row.row_number === rowNumber ? updatedRow : row,
      ),
    })),

  markRowEdited: (rowNumber: number) =>
    set((state) => ({
      editedRows: new Set(state.editedRows).add(rowNumber),
    })),

  clearEditedRows: () => set({ editedRows: new Set<number>() }),

  setValidationResult: (result: CollectionImportResultDTO) => {
    const failed = result.changes.filter(
      (change) => change.action === 'failed',
    );
    const success = result.changes.filter(
      (change) => change.action !== 'failed',
    );

    set({
      validationResult: result,
      failedChanges: failed,
      successChanges: success,
    });
  },

  setValidating: (isValidating: boolean) =>
    set((state) => ({
      import: {
        ...state.import,
        status: {
          ...state.import.status,
          isValidating,
        },
      },
    })),

  update: (data: Partial<Import>) =>
    set((state) => ({
      import: {
        ...state.import,
        ...data,
        status: { ...state.import.status, ...(data.status ?? {}) },
      },
    })),

  reset: () =>
    set({
      import: {},
      importData: [],
      originalImportDataByRowNumber: {},
      validationResult: null,
      failedChanges: [],
      successChanges: [],
      editedRows: new Set<number>(),
    }),
}));
